import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { Task } from '@app/model/task';
import { SubCategoryService } from '../sub-category-service/sub-category.service';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';
import { fetchAllRows } from '@app/utils/fetch-all-rows';

type TaskCreateOptions = {
  asSystemTreasury?: boolean;
};

export type CreateTasksWithDepositResult = {
  success: boolean;
  root_task_id: string;
  task_ids: string[];
  total_cost: number;
};

// 由資料庫 RPC 回傳的業務錯誤，訊息可直接顯示給使用者
export class TaskCreateError extends Error {}

export type AdminForceCancelTaskResult = {
  success: boolean;
  already_cancelled?: boolean;
  task_id: string;
  refunded_amount: number;
  refunded_user_ids: string[];
};

export type AdminForceCompleteTaskResult = {
  success: boolean;
  already_completed?: boolean;
  already_paid?: boolean;
  task_id: string;
  payee_id?: string;
  paid_amount: number;
};

export type CompleteTaskAndPayResult = {
  success: boolean;
  already_completed?: boolean;
  already_paid?: boolean;
  task_id: string;
  payee_id: string;
  paid_amount: number;
};

export type AcceptTaskMatchmakingResult = {
  success: boolean;
  task_id: string;
  acceptor_id: string;
  refunded_amount: number;
  refunded_user_ids: string[];
};

export type AdminTaskActionLog = {
  id: string;
  created_at: string;
  task_id: string | null;
  admin_id: string | null;
  action: 'force_cancel' | 'force_complete';
  reason: string;
  previous_status: string | null;
  new_status: string;
  amount: number;
};

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  private supabaseClient: SupabaseClient;
  
  constructor(
    private subCategoryService: SubCategoryService
  ) {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  getAllPastHoursCreateTasks(hour: number) {
    const pastTime = new Date(Date.now() - hour * 60 * 60 * 1000).toISOString();
    return this.supabaseClient.from('tasks').select('*').gt('createAt', pastTime).then((res) => {
      if (res.error) {
        console.error("Error fetching tasks:", res.error);
        return Promise.reject(res.error);
      }
      return res;
    });
  }

  getTaskById(taskId: any) {
    return this.supabaseClient.from('tasks').select('*').eq('id', taskId).then((res) => {
      return res;
    });
  }

  getTasksByCreatorId(creatorId: any) {
    return this.supabaseClient.from('tasks').select('*').eq('creatorId', creatorId).then((res) => {
      return res;
    });
  }

  getAcceptableTasks() {
    return this.supabaseClient.from('tasks').select('*').eq('isAvailable', true).then((res) => {
      return res;
    });
  }

  getTasksByAcceptorId(acceptorId: any) {
    return this.supabaseClient.from('tasks').select('*').eq('acceptorId', acceptorId).then((res) => {
      return res;
    });
  }

  getAllCompletedTasks() {
    return this.supabaseClient.from('tasks').select('*').eq('isComplete', true).then((res) => {
      return res;
    });
  }

  // 分頁抓完全部任務（供需管理、供需統計使用），避免超過 Supabase 單次回傳上限時被截斷
  getAllTasksForAdmin() {
    return fetchAllRows<Task>((from, to) =>
      this.supabaseClient
        .from('tasks')
        .select('*', { count: 'exact' })
        .order('createAt', { ascending: false })
        .order('id')
        .range(from, to));
  }

  getAdminTaskActionLogs() {
    return this.supabaseClient
      .from('admin_task_action_log')
      .select('*')
      .order('created_at', { ascending: false });
  }

  async adminForceCancelTask(taskId: string, reason: string) {
    const { data, error } = await this.supabaseClient.rpc('admin_force_cancel_task', {
      target_task_id: taskId,
      action_reason: reason.trim()
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as AdminForceCancelTaskResult;
  }

  async adminForceCompleteTask(taskId: string, reason: string) {
    const { data, error } = await this.supabaseClient.rpc('admin_force_complete_task', {
      target_task_id: taskId,
      action_reason: reason.trim()
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as AdminForceCompleteTaskResult;
  }

  async getGroupedAcceptableTasksBySubCategoryIds(subCategoryIds: string[], excludeCreatorId?: string): Promise<Task[]> {
    if (subCategoryIds.length === 0) {
      return [];
    }

    const response = await this.supabaseClient
      .from('tasks')
      .select('*')
      .in('sub_category', subCategoryIds)
      .eq('isAvailable', true);

    if (response.error) {
      console.error('Error getting acceptable tasks:', response.error);
      return [];
    }

    const visibleTasks = (response.data || []).filter((task: Task) =>
      !excludeCreatorId || task.creatorId !== excludeCreatorId
    );

    return this.groupTasksForDisplay(visibleTasks);
  }

  // 發起者從媒合清單選定接受者；供給任務的其他申請者會在同一個交易中退回預扣
  async acceptTask(taskId: string, acceptorId: any) {
    const { data, error } = await this.supabaseClient.rpc('accept_task_matchmaking', {
      target_task_id: taskId,
      target_acceptor_id: acceptorId
    });
    if (error) throw new Error(error.message);
    return data as AcceptTaskMatchmakingResult;
  }

  // 提供服務的一方提交完成：需求由接受者提交，供給由發起者提交
  async finishTask(taskId: any) {
    const { error } = await this.supabaseClient.rpc('submit_task_completion', {
      target_task_id: taskId
    });
    if (error) throw new Error(error.message);
  }

  async completeTaskAndPay(taskId: string) {
    const { data, error } = await this.supabaseClient.rpc('complete_task_and_pay', {
      target_task_id: taskId
    });

    if (error) throw error;
    return data as CompleteTaskAndPayResult;
  }

  async checkIsComplete(taskId: any) {
    return this.supabaseClient.from('tasks').select('isFinished').eq('id', taskId).then((res) => {
      return res;
    });
  }

  // 建立任務（含批次數量）、設定 originalTaskId 與需求預扣，在同一個交易中完成
  async createTasksWithDeposit(task: Task, options: TaskCreateOptions = {}) {
    const { data, error } = await this.supabaseClient.rpc('create_tasks_with_deposit', {
      task_payload: {
        title: task.title,
        image_url: task.image_url,
        description: task.description,
        costTime: task.costTime,
        is_supply: task.is_supply,
        address: task.address,
        deadline: task.deadline,
        sub_category: task.sub_category,
        voice_description_url: task.voice_description_url,
        voice_location_url: task.voice_location_url
      },
      task_quantity: task.quantity || 1,
      as_system_treasury: !!options.asSystemTreasury
    });

    if (error) {
      console.error('Error creating task:', error.message);
      throw new TaskCreateError(error.message);
    }

    const result = data as CreateTasksWithDepositResult | null;
    if (!result?.root_task_id) {
      throw new TaskCreateError('任務建立失敗，資料庫未回傳建立結果');
    }

    return result;
  }

  // 只能編輯尚未進入媒合的任務；需求售價變動時由資料庫補扣或退還預扣差額
  async updateTask(task: Task) {
    const { error } = await this.supabaseClient.rpc('update_task_details', {
      target_task_id: task.id,
      task_payload: {
        title: task.title,
        image_url: task.image_url,
        description: task.description,
        costTime: task.costTime,
        is_supply: task.is_supply,
        address: task.address,
        deadline: task.deadline,
        sub_category: task.sub_category
      }
    });
    if (error) throw new Error(error.message);
  }

  async deleteTask(taskId: any, reason: string = 'task_delete') {
    let data: any;
    let error: any;

    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await this.supabaseClient.rpc('delete_unaccepted_task', {
        target_task_id: taskId,
        delete_reason: reason
      });
      data = res.data;
      error = res.error;

      if (!error || !this.isRetryableDeleteError(error)) {
        break;
      }

      await this.wait(150 * (attempt + 1));
    }

    if (error) {
      throw new Error(error.message);
    }

    const verifyRes = await this.supabaseClient
      .from('tasks')
      .select('id')
      .eq('id', taskId)
      .maybeSingle();

    if (verifyRes.error) {
      throw new Error(verifyRes.error.message);
    }

    if (verifyRes.data) {
      throw new Error('任務刪除後仍存在，請重新整理後再試一次');
    }

    return data as {
      deleted?: boolean;
      task_id?: string;
      refund?: { refunded?: boolean; reason?: string; amount?: number };
      promoted_root_task_id?: string | null;
      reparented_count?: number;
    };
  }

  async deleteTasks(taskIds: string[], reason: string = 'task_group_delete') {
    const uniqueTaskIds = Array.from(new Set(taskIds.filter(Boolean)));
    if (uniqueTaskIds.length === 0) {
      return {
        deleted: true,
        deleted_task_ids: [],
        deleted_count: 0,
        refund: { refunded: false, amount: 0 },
        reparented_count: 0
      };
    }

    let data: any;
    let error: any;

    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await this.supabaseClient.rpc('delete_unaccepted_tasks', {
        target_task_ids: uniqueTaskIds,
        delete_reason: reason
      });
      data = res.data;
      error = res.error;

      if (!error || !this.isRetryableDeleteError(error)) {
        break;
      }

      await this.wait(150 * (attempt + 1));
    }

    if (error) {
      throw new Error(error.message);
    }

    const verifyRes = await this.supabaseClient
      .from('tasks')
      .select('id')
      .in('id', uniqueTaskIds);

    if (verifyRes.error) {
      throw new Error(verifyRes.error.message);
    }

    if ((verifyRes.data ?? []).length > 0) {
      throw new Error('部分任務刪除後仍存在，請重新整理後再試一次');
    }

    return data as {
      deleted?: boolean;
      deleted_task_ids?: string[];
      deleted_count?: number;
      refund?: { refunded?: boolean; reason?: string; amount?: number };
      reparented_count?: number;
    };
  }

  private isRetryableDeleteError(error: { code?: string; message?: string }) {
    return error.code === '40P01' || error.message?.toLowerCase().includes('deadlock detected');
  }

  private wait(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  async sortTasksByTime(tasks: Task[]): Promise<Task[]> {
    return tasks.sort((a, b) => {
      const deadlineA = new Date(a.deadline).getTime();
      const deadlineB = new Date(b.deadline).getTime();
      return deadlineA - deadlineB;
    });
  }

  async sortCreatedSupplyTasks(tasks: Task[]): Promise<Task[]> {
    const statusOrder: { [key: string]: number } = {
      "已接受": 1,
      "已提交": 2,
      "媒合進行中": 3,
      "已發起": 4,
      "已完成": 5,
      "已取消": 6
    };

    return tasks.sort((a, b) => {
      return statusOrder[a.status as string] - statusOrder[b.status as string];
    });
  }


  async sortCreatedNeedsTasks(tasks: Task[]): Promise<Task[]> {
    const statusOrder: { [key: string]: number } = {
      "已接受": 1,
      "已提交": 2,
      "媒合進行中": 3,
      "已發起": 4,
      "已完成": 5,
      "已取消": 6
    };

    return tasks.sort((a, b) => {
      return statusOrder[a.status as string] - statusOrder[b.status as string];
    });
  }

  async sortAcceptedSupplyTasks(tasks: Task[]): Promise<Task[]> {
    const statusOrder: { [key: string]: number } = {
      "已接受": 1,
      "已提交": 2,
      "媒合進行中": 3,
      "已發起": 4,
      "已完成": 5,
      "已取消": 6
    };

    return tasks.sort((a, b) => {
      return statusOrder[a.status as string] - statusOrder[b.status as string];
    });
  }

  async sortAcceptedNeedsTasks(tasks: Task[]): Promise<Task[]> {
    const statusOrder: { [key: string]: number } = {
      "已接受": 1,
      "已提交": 2,
      "媒合進行中": 3,
      "已發起": 4,
      "已完成": 5,
      "已取消": 6
    };

    return tasks.sort((a, b) => {
      return statusOrder[a.status as string] - statusOrder[b.status as string];
    });
  }

  async rateCompletedTask(taskId: string, rate: number) {
    const { data, error } = await this.supabaseClient.rpc('rate_completed_task', {
      target_task_id: taskId,
      new_rating: rate
    });
    if (error) throw error;
    return data;
  }

  async updateFinalTimeCost(taskId: any, finalTimeCost: number) {
    const { data, error } = await this.supabaseClient
      .from('tasks')
      .update({ final_time_cost: finalTimeCost })
      .eq('id', taskId)
      .select('id')
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('任務不存在或您沒有更新權限');
  }

  async getAvailableTaskCount(taskTitle: string, subCategory: any, creatorId: any) {
    const res = await this.supabaseClient
      .from('tasks')
      .select('*')
      .eq('title', taskTitle)
      .eq('sub_category', subCategory)
      .eq('creatorId', creatorId)
      .eq('isAccepted', false);

    if (res?.error) {
      console.error('Error getting available task count:', res.error);
      return 0;
    }

    return res?.data?.length || 0;
  }

  async getTasksByOriginalTaskId(originalTaskId: any) {
    const res = await this.supabaseClient
      .from('tasks')
      .select('*')
      .or(`id.eq.${originalTaskId},originalTaskId.eq.${originalTaskId}`);

    if (res?.error) {
      console.error('Error getting tasks by original task id:', res.error);
      return [];
    }

    return res?.data || [];
  }

  async assignToRandomUser(task: Task) {
    const res = await this.subCategoryService.getActivateUsersIdBySubCategoryId(task.sub_category);
    const users = res?.data?.[0]?.activate_users || [];

    if (users.length === 0) {
      throw new Error("No active users available.");
    }

    const creatorIndex = users.indexOf(task.creatorId);
    if (creatorIndex !== -1) {
      users.splice(creatorIndex, 1);
    }

    if (users.length === 0) {
      throw new Error("No available users to assign the task.");
    }

    const randomIndex = Math.floor(Math.random() * users.length);
    const randomUser = users[randomIndex];

    await this.acceptTask(task.id, randomUser);
  }

  async getGroupedTasksByCreatorId(creatorId: any) {
    const res = await this.supabaseClient.from('tasks').select('*').eq('creatorId', creatorId);
    if (res?.error) {
      console.error('Error getting tasks by creator id:', res.error);
      return { data: [] };
    }
    
    const tasks = res?.data || [];
    const groupedTasks = this.groupTasksForDisplay(tasks);
    return { data: groupedTasks };
  }

  async getGroupedAcceptableTasks() {
    const res = await this.supabaseClient.from('tasks').select('*').eq('isAvailable', true);
    if (res?.error) {
      console.error('Error getting acceptable tasks:', res.error);
      return { data: [] };
    }
    
    const tasks = res?.data || [];
    const groupedTasks = this.groupTasksForDisplay(tasks);
    return { data: groupedTasks };
  }

  private groupTasksForDisplay(tasks: Task[]): Task[] {
    const taskGroups = new Map<string, Task[]>();
    
    // 按照任務類型分組
    tasks.forEach(task => {
      let groupKey: string;
      
      if (task.originalTaskId) {
        // 如果是子任務，使用 originalTaskId 作為分組鍵
        groupKey = `${task.originalTaskId}`;
      } else {
        // 如果是原始任務，使用任務ID作為分組鍵
        groupKey = `${task.id}`;
      }
      
      if (!taskGroups.has(groupKey)) {
        taskGroups.set(groupKey, []);
      }
      taskGroups.get(groupKey)!.push(task);
    });
    
    const displayTasks: Task[] = [];
    
    // 為每個分組創建一個顯示任務
    taskGroups.forEach((taskGroup) => {
      // 選擇原始任務作為代表任務（沒有originalTaskId的任務）
      const originalTask = taskGroup.find(t => !t.originalTaskId);
      const representativeTask = originalTask || taskGroup[0];
      
      // 計算可用庫存（未被接受的任務數量）
      const availableStock = taskGroup.filter(t => !t.isAccepted && !t.acceptorId).length;
      
      // 創建顯示任務，包含庫存信息
      const displayTask = {
        ...representativeTask,
        availableStock: availableStock,
        totalStock: taskGroup.length,
        taskInstances: taskGroup // 保存所有任務實例的引用
      };
      
      displayTasks.push(displayTask);
    });
    
    return displayTasks;
  }

}
